import React, { useState, useEffect, useCallback } from 'react';

// Brand
const PURPLE = '#5A00FF';
const NAVY = '#232353';

const money = (n) =>
  n == null || isNaN(n) ? '—' : `$${Number(n).toFixed(2)}`;

const round2 = (n) => Math.round((Number(n) + Number.EPSILON) * 100) / 100;

export default function AzureInvoicePage() {
  const [file, setFile] = useState(null);
  const [parsing, setParsing] = useState(false);
  const [preview, setPreview] = useState(null); // { invoiceNo, periodLabel, ... rows }
  const [txnDate, setTxnDate] = useState('');
  const [rows, setRows] = useState([]);
  const [pushing, setPushing] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  const [history, setHistory] = useState([]);

  const loadHistory = useCallback(async () => {
    try {
      const r = await fetch('/api/azure/history', { credentials: 'include' });
      if (r.ok) setHistory(await r.json());
    } catch (_) {}
  }, []);

  useEffect(() => { loadHistory(); }, [loadHistory]);

  const handleUpload = async () => {
    if (!file) return;
    setParsing(true);
    setError('');
    setResult(null);
    setPreview(null);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const r = await fetch('/api/azure/upload-invoice', {
        method: 'POST',
        body: fd,
        credentials: 'include',
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data.error || 'Upload failed');
      setPreview(data);
      setTxnDate(data.defaultTxnDate);
      setRows(
        data.rows.map((row) => ({
          ...row,
          include: row.matched && !row.alreadyPushed,
        }))
      );
    } catch (e) {
      setError(e.message);
    } finally {
      setParsing(false);
    }
  };

  const updateMargin = (idx, value) => {
    setRows((prev) =>
      prev.map((r, i) => {
        if (i !== idx) return r;
        const marginPct = value === '' ? '' : Number(value);
        const sellPrice =
          marginPct === '' ? r.sellPrice : round2(r.lcCost * (1 + marginPct / 100));
        return { ...r, marginPct, sellPrice };
      })
    );
  };

  const toggleInclude = (idx) =>
    setRows((prev) => prev.map((r, i) => (i === idx ? { ...r, include: !r.include } : r)));

  const selected = rows.filter((r) => r.include && r.qbCustomerId);
  const selectedTotal = selected.reduce((s, r) => s + Number(r.sellPrice || 0), 0);
  const selectedCost = selected.reduce((s, r) => s + Number(r.lcCost || 0), 0);

  const handlePush = async () => {
    if (!selected.length) return;
    if (!window.confirm(
      `Push ${selected.length} Azure invoice(s) to QuickBooks?\n` +
      `Total (ex GST): ${money(selectedTotal)}\nDate: ${txnDate}`
    )) return;

    setPushing(true);
    setError('');
    try {
      const r = await fetch('/api/azure/push-invoices', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          invoiceNo: preview.invoiceNo,
          periodLabel: preview.periodLabel,
          txnDate,
          rows: selected.map((r) => ({
            lcCustomerName: r.lcCustomerName,
            lcAccountId: r.lcAccountId,
            qbCustomerId: r.qbCustomerId,
            marginPct: Number(r.marginPct || 15),
            lcCost: r.lcCost,
            sellPrice: r.sellPrice,
          })),
        }),
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data.error || 'Push failed');
      setResult(data);
      // mark pushed rows so they can't be double-sent
      const pushedNames = new Set(
        data.results.filter((x) => x.status === 'pushed').map((x) => x.lcCustomerName)
      );
      setRows((prev) =>
        prev.map((r) =>
          pushedNames.has(r.lcCustomerName)
            ? { ...r, alreadyPushed: true, include: false }
            : r
        )
      );
      loadHistory();
    } catch (e) {
      setError(e.message);
    } finally {
      setPushing(false);
    }
  };

  return (
    <div className="container-fluid py-4" style={{ maxWidth: 1100 }}>
      <div className="d-flex align-items-center mb-3">
        <span
          style={{
            display: 'inline-block',
            width: 3,
            height: 28,
            background: PURPLE,
            marginRight: 12,
          }}
        />
        <h2 className="m-0" style={{ color: NAVY, fontFamily: 'Montserrat, sans-serif' }}>
          Azure Invoices
        </h2>
      </div>
      <p className="text-muted">
        Upload the monthly LeaderCloud Azure tax invoice PDF. The portal reads each
        customer’s total, applies their margin, and posts one “Azure Services” invoice
        per customer to QuickBooks as a draft for review.
      </p>

      {/* Upload card */}
      <div className="card mb-4">
        <div className="card-body d-flex flex-wrap align-items-center gap-3">
          <input
            type="file"
            accept="application/pdf"
            className="form-control"
            style={{ maxWidth: 360 }}
            onChange={(e) => setFile(e.target.files[0] || null)}
          />
          <button
            className="btn"
            style={{ background: PURPLE, color: '#fff' }}
            disabled={!file || parsing}
            onClick={handleUpload}
          >
            {parsing ? 'Reading PDF…' : 'Read invoice'}
          </button>
          {file && <span className="text-muted small">{file.name}</span>}
        </div>
      </div>

      {error && <div className="alert alert-danger">{error}</div>}

      {/* Preview */}
      {preview && (
        <div className="card mb-4">
          <div className="card-header d-flex flex-wrap justify-content-between align-items-center">
            <div>
              <strong>LC Invoice {preview.invoiceNo}</strong>{' '}
              <span className="text-muted">
                · {preview.periodLabel} · dated {preview.invoiceDate}
              </span>
            </div>
            <div className="d-flex align-items-center gap-2">
              <label className="small text-muted m-0">QB invoice date</label>
              <input
                type="date"
                className="form-control form-control-sm"
                style={{ width: 160 }}
                value={txnDate}
                onChange={(e) => setTxnDate(e.target.value)}
              />
            </div>
          </div>
          <div className="table-responsive">
            <table className="table table-hover align-middle m-0">
              <thead style={{ background: '#f6f5ff' }}>
                <tr>
                  <th style={{ width: 40 }}></th>
                  <th>Customer</th>
                  <th className="text-end">LC Cost (ex GST)</th>
                  <th className="text-center" style={{ width: 120 }}>Margin %</th>
                  <th className="text-end">Sell Price (ex GST)</th>
                  <th>QB Customer</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r, i) => (
                  <tr key={i} className={!r.matched ? 'table-warning' : undefined}>
                    <td>
                      <input
                        type="checkbox"
                        checked={!!r.include}
                        disabled={!r.qbCustomerId || r.alreadyPushed}
                        onChange={() => toggleInclude(i)}
                      />
                    </td>
                    <td>{r.lcCustomerName}</td>
                    <td className="text-end">{money(r.lcCost)}</td>
                    <td className="text-center">
                      <input
                        type="number"
                        className="form-control form-control-sm text-center"
                        style={{ width: 90, margin: '0 auto' }}
                        value={r.marginPct}
                        min={0}
                        step={1}
                        onChange={(e) => updateMargin(i, e.target.value)}
                      />
                    </td>
                    <td className="text-end fw-bold">{money(r.sellPrice)}</td>
                    <td>
                      {r.qbCustomerId ? (
                        <span className="text-muted">QB #{r.qbCustomerId}</span>
                      ) : (
                        <span className="text-danger small">
                          Unmapped — add in Customers page
                        </span>
                      )}
                    </td>
                    <td>
                      {r.alreadyPushed ? (
                        <span className="badge bg-secondary">Already pushed</span>
                      ) : r.matched ? (
                        <span className="badge bg-success-subtle text-success">Ready</span>
                      ) : (
                        <span className="badge bg-warning text-dark">No mapping</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr style={{ background: '#faf9ff' }}>
                  <td></td>
                  <td className="fw-bold">
                    {selected.length} selected
                  </td>
                  <td className="text-end fw-bold">{money(selectedCost)}</td>
                  <td></td>
                  <td className="text-end fw-bold" style={{ color: PURPLE }}>
                    {money(selectedTotal)}
                  </td>
                  <td colSpan={2}>
                    <span className="text-muted small">
                      Profit: {money(selectedTotal - selectedCost)} · GST added by QB on top
                    </span>
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
          <div className="card-footer d-flex justify-content-end">
            <button
              className="btn"
              style={{ background: PURPLE, color: '#fff' }}
              disabled={!selected.length || pushing}
              onClick={handlePush}
            >
              {pushing ? 'Pushing…' : `Push ${selected.length} to QuickBooks`}
            </button>
          </div>
        </div>
      )}

      {/* Result */}
      {result && (
        <div className="alert alert-success">
          <strong>Done — {result.pushed} of {result.total} pushed.</strong>
          <ul className="mb-0 mt-2">
            {result.results.map((x, i) => (
              <li key={i}>
                {x.lcCustomerName}:{' '}
                {x.status === 'pushed' && (
                  <>QB {x.qbDocNumber || x.qbInvoiceId} · {money(x.sellPrice)}</>
                )}
                {x.status === 'skipped' && <em>skipped ({x.reason})</em>}
                {x.status === 'error' && (
                  <span className="text-danger">error: {x.error}</span>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* History */}
      {history.length > 0 && (
        <div className="card">
          <div className="card-header"><strong>Recent Azure pushes</strong></div>
          <div className="table-responsive">
            <table className="table table-sm m-0">
              <thead>
                <tr>
                  <th>LC Inv</th><th>Customer</th><th>Period</th>
                  <th className="text-end">Cost</th><th className="text-center">Margin</th>
                  <th className="text-end">Sell</th><th>QB Doc</th><th>Status</th><th>When</th>
                </tr>
              </thead>
              <tbody>
                {history.map((h, i) => (
                  <tr key={i}>
                    <td>{h.lc_invoice_no}</td>
                    <td>{h.lc_customer_name}</td>
                    <td>{h.period_label}</td>
                    <td className="text-end">{money(h.lc_cost)}</td>
                    <td className="text-center">{h.margin_pct}%</td>
                    <td className="text-end">{money(h.sell_price)}</td>
                    <td>{h.qb_doc_number || '—'}</td>
                    <td>
                      {h.status === 'pushed'
                        ? <span className="badge bg-success-subtle text-success">pushed</span>
                        : <span className="badge bg-danger-subtle text-danger">{h.status}</span>}
                    </td>
                    <td className="small text-muted">
                      {h.pushed_at ? new Date(h.pushed_at).toLocaleString() : ''}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
